import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { toJS } from 'mobx';
import { Capacitor } from '@capacitor/core';
import { bookStore, userStore, downloadStore, libraryStore, wishlistStore } from '../../store';
import {
  Loader,
  LoginPromptModal,
  ImagePreviewModal,
  dynamicIsland,
  showToast,
} from '../../components/shared';
import RateReviewSheet from '../../components/shared/RateReviewSheet';
import { hitApi } from '../../database/services/ApiHelper';
import { API } from '../../database/apis';
import {
  ArrowLeft,
  Heart,
  Share2,
  MoreVertical,
  BookOpen,
  ShoppingCart,
  Check,
  Star,
  Edit3,
} from 'lucide-react';

// Book detail screen — v2 design (2026-05-30 evening).
//
// Layout:
//   1. Top bar: back + heart + share + menu (icon-only, on white).
//   2. Centered cover (140 x 200, soft shadow).
//   3. Title (centered, 22px bold), author (15px muted).
//   4. Rating row: ★ 4.6 (12.5K) + "Self Help" chip.
//   5. 4-stat strip: Pages | Language | Publisher | Downloads.
//   6. Tabs: About / Reviews / Similar Books.
//   7. Sticky bottom CTAs: indigo "Read Free" + white outlined "Buy Ad-free Copy".

// Apple App Store rules: digital goods sold in iOS apps must use IAP and
// pay Apple 30%. Until we add IAP, hide the Buy button on iOS Capacitor
// builds — the rest of the app stays unaffected. Web and Android keep it.
const isIosNative = (() => {
  try {
    return Capacitor.getPlatform() === 'ios' && Capacitor.isNativePlatform();
  } catch {
    return false;
  }
})();

const BRAND = '#5B47E0';
const BRAND_DARK = '#4A37CC';
const TEXT_PRIMARY = '#0F172A';
const TEXT_MUTED = '#64748B';
const SURFACE = '#F1F5F9';
const STAR_YELLOW = '#FFB800';

type Tab = 'about' | 'reviews' | 'similar';

interface BookDetail {
  publisher?: string | null;
  downloadCount?: number;
  averageRating?: number | null;
  reviewCount?: number;
  // Stat-strip fields that arrive from /books/[id]. Override whatever
  // the leaner adverBooks listing carried.
  pageCount?: number | null;
  language?: string;
  adFreePriceCents?: number;
}

const BookScreen: React.FC = observer(() => {
  const navigate = useNavigate();
  const book = bookStore.selectedBook;

  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  const [showImagePreview, setShowImagePreview] = useState(false);
  const [showRateSheet, setShowRateSheet] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [tab, setTab] = useState<Tab>('about');

  // Hydrate v2-stat-strip fields from the detail endpoint. The base Book
  // shape in MobX doesn't carry publisher / downloadCount / aggregate
  // ratings, so we fetch them on mount. Falls back gracefully on failure.
  const [detail, setDetail] = useState<BookDetail | null>(null);
  const [similar, setSimilar] = useState<any[]>([]);
  const [reviews, setReviews] = useState<any[]>([]);
  const [userReview, setUserReview] = useState<any>(null);

  useEffect(() => {
    if (!book?._id) return;
    void fetchDetail(book._id);
    void fetchSimilar(book._id);
    if (userStore.user?._id) void fetchUserReview(book._id, userStore.user._id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [book?._id]);

  async function fetchDetail(id: string) {
    try {
      const token = userStore.authToken || userStore.deviceToken;
      const res: any = await hitApi({
        endpoint: API.BOOK_DETAIL(id),
        method: 'GET',
        token,
      });
      setDetail({
        publisher: res?.publisher,
        downloadCount: res?.downloadCount ?? 0,
        averageRating: res?.averageRating,
        reviewCount: res?.reviewCount ?? 0,
        pageCount: res?.pageCount,
        language: res?.language,
        adFreePriceCents: res?.adFreePriceCents,
      });
    } catch (e) {
      console.log('[Book] detail fetch failed', e);
    }
  }

  async function fetchSimilar(id: string) {
    try {
      const token = userStore.authToken || userStore.deviceToken;
      const res: any = await hitApi({
        endpoint: `api/d2p/kleverbook/books/${id}/similar?limit=8`,
        method: 'GET',
        token,
      });
      setSimilar(Array.isArray(res?.books) ? res.books : []);
    } catch (e) {
      console.log('[Book] similar fetch failed', e);
      setSimilar([]);
    }
  }

  async function fetchUserReview(bookId: string, userId: string) {
    try {
      const token = userStore.authToken;
      const res: any = await hitApi({
        endpoint: `api/d2p/books/${bookId}/review?userId=${encodeURIComponent(userId)}`,
        method: 'GET',
        token,
      });
      setUserReview(res?.review ?? null);
    } catch (e) {
      // Quiet — user has no review yet is also "fetch failed" in the
      // logged-out branch.
    }
  }

  async function loadReviews() {
    if (!book?._id) return;
    try {
      const token = userStore.authToken || userStore.deviceToken;
      const res: any = await hitApi({
        endpoint: `api/d2p/books/${book._id}/review?limit=20`,
        method: 'GET',
        token,
      });
      setReviews(Array.isArray(res?.reviews) ? res.reviews : []);
    } catch (e) {
      setReviews([]);
    }
  }

  // Lazy-load reviews only when the user opens the Reviews tab.
  useEffect(() => {
    if (tab === 'reviews') void loadReviews();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  if (!book) {
    navigate('/app/home', { replace: true });
    return null;
  }

  const coverUrl = book.coverImage || '';
  const owned = libraryStore.ownsBook(book._id);
  const categoryName =
    typeof book.category === 'object' ? book.category.name : book.categoryName || 'Uncategorized';

  // === handlers ===

  const handleWishlist = async () => {
    if (!userStore.isLoggedIn) {
      setShowLoginPrompt(true);
      return;
    }
    const wasIn = wishlistStore.has(book._id);
    const ok = await wishlistStore.toggle(book);
    if (ok) {
      showToast(
        wasIn ? 'Removed from wishlist' : 'Added to wishlist',
        wasIn ? 'info' : 'success',
        { subtitle: book.title },
      );
    } else {
      showToast('Wishlist sync failed', 'error', { subtitle: 'Please try again' });
    }
  };

  const handleShare = async () => {
    // Capacitor's Share plugin exists; until we wire it, fall back to
    // copying a link to the clipboard so the share button is functional.
    const url = `${window.location.origin}/#/book?id=${encodeURIComponent(book._id)}`;
    try {
      await navigator.clipboard.writeText(url);
      showToast('Link copied', 'success', { subtitle: book.title });
    } catch {
      showToast('Could not copy link', 'error');
    }
  };

  const handleDownload = () => {
    if (!userStore.isLoggedIn) {
      setShowLoginPrompt(true);
      return;
    }
    setDownloading(true);
    downloadStore.downloadBook(book, (success, message) => {
      setDownloading(false);
      if (success) {
        dynamicIsland.show({
          kind: 'reading',
          title: 'Added to your library',
          subtitle: book.title,
          onClick: () => navigate('/app/library'),
        });
      } else {
        showToast('Download failed', 'error', { subtitle: message || 'Please try again' });
      }
    });
  };

  const handleBuy = async () => {
    if (!userStore.isLoggedIn) {
      setShowLoginPrompt(true);
      return;
    }
    try {
      const result = await libraryStore.startCheckout(book._id);
      if (!result?.url) {
        showToast('Checkout failed', 'error', { subtitle: 'Please try again' });
        return;
      }
      window.location.href = result.url;
    } catch (e: any) {
      if (e?.data?.alreadyOwned) {
        libraryStore.getOwnedLibrary().catch(() => {});
        showToast('You already own this book', 'info', { subtitle: 'Open it from your library' });
      } else {
        showToast('Checkout failed', 'error', { subtitle: e?.message || 'Please try again' });
      }
    }
  };

  const handleReadOwned = () => {
    const entry = libraryStore.ownedBookEntry(book._id);
    if (!entry) return;
    navigate('/pdf-viewer', {
      state: {
        download: {
          _id: entry._id,
          user: userStore.user?._id ?? '',
          adverbook: toJS(entry.book),
          createdAt: entry.purchasedAt,
        },
        owned: true,
      },
    });
  };

  // === render ===

  const adFreePriceUsd =
    typeof detail?.adFreePriceCents === 'number'
      ? `$${(detail.adFreePriceCents / 100).toFixed(2)}`
      : null;

  return (
    <div className="h-full flex flex-col" style={{ background: '#FFFFFF' }}>
      <Loader visible={downloading} message="Downloading..." />
      <LoginPromptModal
        visible={showLoginPrompt}
        onClose={() => setShowLoginPrompt(false)}
        message="Please login to download this book."
      />
      <ImagePreviewModal
        visible={showImagePreview}
        imageUrl={coverUrl}
        onClose={() => setShowImagePreview(false)}
      />
      <RateReviewSheet
        open={showRateSheet}
        bookId={book._id}
        bookTitle={book.title || 'this book'}
        existing={userReview}
        onClose={() => setShowRateSheet(false)}
        onSubmitted={(review: any) => {
          setUserReview(review);
          void fetchDetail(book._id); // refresh averageRating + reviewCount
          if (tab === 'reviews') void loadReviews();
        }}
      />

      {/* === Top bar === */}
      <div className="safe-area-top" style={{ background: '#FFFFFF' }}>
        <div className="px-5 pt-4 pb-3 flex items-center justify-between">
          <button
            onClick={() => navigate(-1)}
            aria-label="Back"
            className="w-10 h-10 rounded-full flex items-center justify-center transition-colors"
            style={{ background: SURFACE }}
          >
            <ArrowLeft className="w-5 h-5" style={{ color: TEXT_PRIMARY }} />
          </button>
          <div className="flex items-center gap-2">
            <IconButton
              aria-label={wishlistStore.has(book._id) ? 'Remove from wishlist' : 'Add to wishlist'}
              onClick={handleWishlist}
            >
              <Heart
                className="w-[18px] h-[18px]"
                style={{
                  color: wishlistStore.has(book._id) ? '#EF4444' : TEXT_PRIMARY,
                  fill: wishlistStore.has(book._id) ? '#EF4444' : 'transparent',
                }}
              />
            </IconButton>
            <IconButton aria-label="Share" onClick={handleShare}>
              <Share2 className="w-[18px] h-[18px]" style={{ color: TEXT_PRIMARY }} />
            </IconButton>
            <IconButton aria-label="More options" onClick={() => {}}>
              <MoreVertical className="w-[18px] h-[18px]" style={{ color: TEXT_PRIMARY }} />
            </IconButton>
          </div>
        </div>
      </div>

      {/* === Body (scrolls) === */}
      <div className="flex-1 overflow-y-auto pb-32">
        {/* Cover */}
        <div className="flex justify-center pt-2 pb-7">
          <button
            onClick={() => coverUrl && setShowImagePreview(true)}
            className="w-[140px] h-[200px] rounded-2xl overflow-hidden"
            style={{
              boxShadow: '0 20px 40px -12px rgba(15, 23, 42, 0.25)',
              background: SURFACE,
            }}
          >
            {coverUrl ? (
              <img src={coverUrl} alt={book.title} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <span className="text-[40px] font-bold" style={{ color: TEXT_MUTED }}>
                  {book.title?.charAt(0) || 'B'}
                </span>
              </div>
            )}
          </button>
        </div>

        {/* Title + author */}
        <div className="px-6 text-center">
          <h1
            className="text-[24px] font-bold tracking-tight leading-tight"
            style={{ color: TEXT_PRIMARY }}
          >
            {book.title}
          </h1>
          <p className="text-[15px] mt-1.5" style={{ color: TEXT_MUTED }}>
            {book.authorName}
          </p>
        </div>

        {/* Rating + category */}
        <div className="flex items-center justify-center gap-3 mt-4 px-6">
          <RatingPill
            average={detail?.averageRating}
            count={detail?.reviewCount}
          />
          <CategoryPill label={categoryName} />
        </div>

        {/* Stat strip — detail (from /books/[id]) overrides the leaner
            shape carried in `book` (from /kleverbook/books list). */}
        <StatStrip
          pageCount={detail?.pageCount ?? (book as any).pageCount ?? null}
          language={detail?.language ?? (book as any).language ?? 'English'}
          publisher={detail?.publisher ?? null}
          downloadCount={detail?.downloadCount}
        />

        {/* Tabs */}
        <div className="px-5 mt-3">
          <div className="flex items-center" style={{ borderBottom: '1px solid #F1F5F9' }}>
            <TabButton active={tab === 'about'} onClick={() => setTab('about')} label="About" />
            <TabButton
              active={tab === 'reviews'}
              onClick={() => setTab('reviews')}
              label={`Reviews${detail?.reviewCount ? ` (${formatCount(detail.reviewCount)})` : ''}`}
            />
            <TabButton
              active={tab === 'similar'}
              onClick={() => setTab('similar')}
              label="Similar Books"
            />
          </div>

          <div className="pt-5">
            {tab === 'about' && (
              <AboutTab description={book.description || (book as any).story} />
            )}
            {tab === 'reviews' && (
              <ReviewsTab
                reviews={reviews}
                userReview={userReview}
                onWriteReview={() => {
                  if (!userStore.isLoggedIn) {
                    setShowLoginPrompt(true);
                    return;
                  }
                  setShowRateSheet(true);
                }}
              />
            )}
            {tab === 'similar' && (
              <SimilarTab
                books={similar}
                onOpen={(b) => {
                  bookStore.setSelectedBook({
                    _id: b.id,
                    title: b.title,
                    authorName: b.authorName,
                    category: b.category || '',
                    coverImage: b.coverUrl || '',
                    description: '',
                    ad_slots: [],
                  } as any);
                  navigate('/book');
                }}
              />
            )}
          </div>
        </div>
      </div>

      {/* === Sticky bottom actions === */}
      <div
        className="absolute bottom-0 left-0 right-0 safe-area-bottom px-5 pt-3 pb-4 space-y-2.5"
        style={{
          background:
            'linear-gradient(180deg, rgba(255,255,255,0) 0%, rgba(255,255,255,1) 30%, rgba(255,255,255,1) 100%)',
        }}
      >
        {owned ? (
          <button
            onClick={handleReadOwned}
            className="w-full h-12 rounded-2xl text-white text-[15px] font-semibold flex items-center justify-center gap-2 transition-transform active:scale-[0.98]"
            style={{
              background: `linear-gradient(135deg, ${BRAND} 0%, ${BRAND_DARK} 100%)`,
              boxShadow: '0 12px 28px -8px rgba(91,71,224,0.5)',
            }}
          >
            <Check className="w-[18px] h-[18px]" />
            You own this — Read now
          </button>
        ) : (
          <>
            <button
              onClick={handleDownload}
              className="w-full h-12 rounded-2xl text-white text-[15px] font-semibold flex items-center justify-center gap-2 transition-transform active:scale-[0.98]"
              style={{
                background: `linear-gradient(135deg, ${BRAND} 0%, ${BRAND_DARK} 100%)`,
                boxShadow: '0 12px 28px -8px rgba(91,71,224,0.5)',
              }}
            >
              <BookOpen className="w-[18px] h-[18px]" />
              Read Free
            </button>

            {!isIosNative && (
              <button
                onClick={handleBuy}
                disabled={libraryStore.checkoutLoading}
                className="w-full h-12 rounded-2xl text-[14px] font-semibold flex items-center justify-center gap-2 transition-colors active:bg-[#F8FAFC]"
                style={{
                  background: '#FFFFFF',
                  color: TEXT_PRIMARY,
                  border: '1px solid #E2E8F0',
                }}
              >
                <ShoppingCart className="w-[16px] h-[16px]" style={{ color: TEXT_MUTED }} />
                {libraryStore.checkoutLoading
                  ? 'Opening checkout…'
                  : `Buy Ad-free Copy${adFreePriceUsd ? ` · ${adFreePriceUsd}` : ''}`}
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
});

/* ====================================================================
 *  Subcomponents
 * ==================================================================== */

function IconButton({
  children,
  onClick,
  ...rest
}: React.ButtonHTMLAttributes<HTMLButtonElement> & { children: React.ReactNode }) {
  return (
    <button
      onClick={onClick}
      className="w-10 h-10 rounded-full flex items-center justify-center transition-colors"
      style={{ background: SURFACE }}
      {...rest}
    >
      {children}
    </button>
  );
}

function RatingPill({ average, count }: { average?: number | null; count?: number }) {
  if (!average || average <= 0) {
    return (
      <span
        className="inline-flex items-center gap-1.5 text-[14px]"
        style={{ color: TEXT_MUTED }}
      >
        <Star className="w-[14px] h-[14px]" style={{ color: '#CBD5E1' }} />
        No reviews yet
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 text-[14px]" style={{ color: TEXT_PRIMARY }}>
      <Star className="w-[14px] h-[14px]" style={{ color: STAR_YELLOW, fill: STAR_YELLOW }} />
      <span className="font-semibold">{average.toFixed(1)}</span>
      {typeof count === 'number' && count > 0 && (
        <span style={{ color: TEXT_MUTED }}>({formatCount(count)})</span>
      )}
    </span>
  );
}

function CategoryPill({ label }: { label: string }) {
  return (
    <span
      className="inline-flex items-center h-7 px-3 rounded-full text-[12.5px] font-medium"
      style={{ background: SURFACE, color: TEXT_PRIMARY }}
    >
      {label}
    </span>
  );
}

function StatStrip({
  pageCount,
  language,
  publisher,
  downloadCount,
}: {
  pageCount: number | null;
  language: string;
  publisher: string | null;
  downloadCount?: number;
}) {
  return (
    <div className="mt-6 mx-5 px-2 py-4 grid grid-cols-4 gap-1 rounded-2xl" style={{ borderTop: '1px solid #F1F5F9', borderBottom: '1px solid #F1F5F9' }}>
      <Stat value={pageCount ? String(pageCount) : '—'} label="Pages" />
      <Stat value={language} label="Language" />
      <Stat value={publisher || 'Independent'} label="Publisher" />
      <Stat value={typeof downloadCount === 'number' ? formatCount(downloadCount) : '—'} label="Downloads" />
    </div>
  );
}

function Stat({ value, label }: { value: string; label: string }) {
  return (
    <div className="text-center">
      <p
        className="text-[15px] font-bold truncate"
        style={{ color: TEXT_PRIMARY }}
        title={value}
      >
        {value}
      </p>
      <p className="text-[11px] mt-0.5" style={{ color: TEXT_MUTED }}>{label}</p>
    </div>
  );
}

function TabButton({
  active,
  onClick,
  label,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
}) {
  return (
    <button
      onClick={onClick}
      className="flex-1 py-3 text-[14px] font-semibold transition-colors relative"
      style={{ color: active ? BRAND : TEXT_MUTED }}
    >
      {label}
      {active && (
        <span
          className="absolute left-0 right-0 bottom-[-1px] h-[2px]"
          style={{ background: BRAND }}
          aria-hidden="true"
        />
      )}
    </button>
  );
}

function AboutTab({ description }: { description?: string }) {
  return (
    <div>
      <p
        className="text-[13px] font-semibold uppercase tracking-wider mb-2"
        style={{ color: TEXT_MUTED }}
      >
        About this book
      </p>
      {description ? (
        <p className="text-[14.5px] leading-relaxed" style={{ color: '#334155' }}>
          {description}
        </p>
      ) : (
        <p className="text-[14px] py-4 text-center" style={{ color: TEXT_MUTED }}>
          No description available yet.
        </p>
      )}
    </div>
  );
}

function ReviewsTab({
  reviews,
  userReview,
  onWriteReview,
}: {
  reviews: any[];
  userReview: any;
  onWriteReview: () => void;
}) {
  return (
    <div>
      {/* CTA: write a review */}
      <button
        onClick={onWriteReview}
        className="w-full flex items-center justify-between p-4 rounded-2xl mb-4 transition-colors active:bg-[#F8F5FE]"
        style={{ background: '#FAFAFD', border: '1px solid #E2DFF5' }}
      >
        <span className="flex items-center gap-3">
          <span
            className="w-9 h-9 rounded-xl flex items-center justify-center"
            style={{ background: '#EDEAFC' }}
          >
            <Edit3 className="w-[16px] h-[16px]" style={{ color: BRAND }} />
          </span>
          <span className="text-left">
            <span className="block text-[14px] font-semibold" style={{ color: TEXT_PRIMARY }}>
              {userReview ? 'Edit your review' : 'Write a review'}
            </span>
            <span className="block text-[12px]" style={{ color: TEXT_MUTED }}>
              {userReview
                ? `Your ${userReview.rating}★ review`
                : 'Share what you thought of this book'}
            </span>
          </span>
        </span>
        <span className="text-[13px] font-semibold" style={{ color: BRAND }}>
          {userReview ? 'Edit' : 'Rate'}
        </span>
      </button>

      {reviews.length === 0 ? (
        <p className="text-[14px] py-8 text-center" style={{ color: TEXT_MUTED }}>
          Be the first to review this book.
        </p>
      ) : (
        <div className="space-y-4">
          {reviews.map((r) => (
            <div key={r.id} className="py-3" style={{ borderBottom: '1px solid #F1F5F9' }}>
              <div className="flex items-center gap-2.5">
                <div
                  className="w-9 h-9 rounded-full flex items-center justify-center text-[13px] font-bold"
                  style={{ background: '#EDEAFC', color: BRAND }}
                >
                  {(r.user?.name || '?').charAt(0).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[14px] font-semibold truncate" style={{ color: TEXT_PRIMARY }}>
                    {r.user?.name || 'Anonymous'}
                  </p>
                  <div className="flex items-center gap-1 mt-0.5">
                    {[1, 2, 3, 4, 5].map((n) => (
                      <Star
                        key={n}
                        className="w-[12px] h-[12px]"
                        style={{
                          color: n <= r.rating ? STAR_YELLOW : '#CBD5E1',
                          fill: n <= r.rating ? STAR_YELLOW : 'transparent',
                        }}
                      />
                    ))}
                  </div>
                </div>
              </div>
              {r.comment && (
                <p className="text-[14px] mt-2 leading-relaxed" style={{ color: '#334155' }}>
                  {r.comment}
                </p>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function SimilarTab({ books, onOpen }: { books: any[]; onOpen: (b: any) => void }) {
  if (books.length === 0) {
    return (
      <p className="text-[14px] py-8 text-center" style={{ color: TEXT_MUTED }}>
        No similar books yet. Check back as more are published.
      </p>
    );
  }
  return (
    <div>
      {books.map((b) => (
        <button
          key={b.id}
          onClick={() => onOpen(b)}
          className="w-full flex items-center gap-3 py-3 text-left transition-colors active:bg-[#F8FAFC]"
          style={{ borderBottom: '1px solid #F1F5F9' }}
        >
          <div className="w-14 h-[72px] rounded-xl flex-shrink-0 overflow-hidden" style={{ background: SURFACE }}>
            {b.coverUrl && <img src={b.coverUrl} alt={b.title} className="w-full h-full object-cover" />}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[14.5px] font-semibold leading-tight truncate" style={{ color: TEXT_PRIMARY }}>
              {b.title}
            </p>
            <p className="text-[13px] mt-0.5 truncate" style={{ color: TEXT_MUTED }}>
              {b.authorName}
            </p>
            {typeof b.averageRating === 'number' && b.averageRating > 0 && (
              <div className="flex items-center gap-1 mt-1">
                <Star className="w-[11px] h-[11px]" style={{ color: STAR_YELLOW, fill: STAR_YELLOW }} />
                <span className="text-[11.5px] font-semibold" style={{ color: TEXT_PRIMARY }}>
                  {b.averageRating.toFixed(1)}
                </span>
                {b.reviewCount > 0 && (
                  <span className="text-[11.5px]" style={{ color: TEXT_MUTED }}>
                    ({formatCount(b.reviewCount)})
                  </span>
                )}
              </div>
            )}
          </div>
        </button>
      ))}
    </div>
  );
}

function formatCount(n: number): string {
  if (n < 1000) return String(n);
  if (n < 10_000) return `${(n / 1000).toFixed(1)}K`;
  if (n < 1_000_000) return `${Math.round(n / 1000)}K`;
  return `${(n / 1_000_000).toFixed(1)}M`;
}

export default BookScreen;
